import React from 'react'

import { Inventory2Outlined } from '@mui/icons-material'
import { Box, Chip, Skeleton, Typography } from '@mui/material'
import { useSelector } from 'react-redux'

import Accordion from '../../../../../../../../../../shared/components/Accordion/Accordion.component'
import EmptyState from '../../../../../../../../../../shared/components/EmptyState/EmptyState.component'
import ItemRequestCard from '../../../../../UserDetails/components/OrderDetails/components/ItemRequestCard/ItemRequestCard.component'

import { getTripOfferRequestsForAdmin } from '../../../../../../../../../../redux/states/admin/trips/getTripOfferRequestsForAdmin/getTripOfferRequestsForAdmin.slice'
import {
  GetTripOfferRequestsForAdminFulfillmentOrderSummary,
  GetTripOfferRequestsForAdminRequestPayload
} from '../../../../../../../../../../redux/states/admin/trips/getTripOfferRequestsForAdmin/getTripOfferRequestsForAdmin.types'
import { RootState } from '../../../../../../../../../../redux/store/store.types'
import { stripPrefixFromId } from '../../../../../../../../../../shared/functions/ID/id.functions'
import status from '../../../../../../../../../../shared/functions/Status/status.dictionaries'
import { getStatusColor } from '../../../../../../../../../../shared/functions/Status/status.functions'
import {
  formatMoney,
  joinElementsWithBullet,
  joinStringsWithBullet
} from '../../../../../../../../../../shared/functions/String/string.functions'
import { useAppDispatch } from '../../../../../../../../../../shared/hooks/redux.hooks'
import linkStyles from '../../../../../../../../../../shared/styles/link.styles'
import styles from './OfferRequestsAccordion.styles'
import { OfferRequestsAccordionProps } from './OfferRequestsAccordion.types'

const GRAMS_PER_KILOGRAM = 1000

const formatTotalWeight = (totalGrams: number | null): string | null => {
  if (totalGrams == null) return null
  if (totalGrams >= GRAMS_PER_KILOGRAM) {
    const kilograms = totalGrams / GRAMS_PER_KILOGRAM
    const rounded = Math.round(kilograms * 10) / 10

    return `${rounded % 1 === 0 ? Math.round(rounded) : rounded} kg`
  }

  return `${Math.round(totalGrams)} g`
}

const OfferRequestsAccordion: React.FC<OfferRequestsAccordionProps> = (props) => {
  const { tripId } = props

  const dispatch = useAppDispatch()
  const { loading, success, error } = useSelector((state: RootState) => state.getTripOfferRequestsForAdmin)

  const handleReload = () => {
    const payload: GetTripOfferRequestsForAdminRequestPayload = {
      request: { flightItineraryId: tripId, page: 1, size: 10 }
    }

    dispatch(getTripOfferRequestsForAdmin(payload))
  }

  if (loading) {
    return (
      <Box sx={styles.skeletonContainer}>
        <Skeleton variant="text" width="40%" height={45} />
        <Skeleton variant="rectangular" height={65} sx={styles.skeletonItem} />
      </Box>
    )
  } else if (error) {
    return (
      <EmptyState
        title="Failed to load offer requests"
        button={{ text: 'Try Again', onClick: handleReload }}
        errorId={error.correlationId}
      />
    )
  } else if (success?.data.offerRequests) {
    const offerRequests = success.data.offerRequests
    let content = null

    const renderFulfillmentOrderAccordion = (
      offerRequestId: string,
      fulfillmentOrder: GetTripOfferRequestsForAdminFulfillmentOrderSummary
    ) => {
      const feeLabel = `Fee ${formatMoney(fulfillmentOrder.totalFeeCents / 100, fulfillmentOrder.currency)}`
      const weightLabel = formatTotalWeight(fulfillmentOrder.totalWeightGrams)
      const itemCountLabel = `${fulfillmentOrder.itemCount} ${fulfillmentOrder.itemCount === 1 ? 'item' : 'items'}`

      const header = (
        <Box sx={styles.fulfillmentHeader}>
          <Box sx={styles.fulfillmentHeaderTitleRow}>
            <Typography variant="body1" sx={styles.fulfillmentHeaderTitle}>
              Order #{stripPrefixFromId(fulfillmentOrder.orderId)}
            </Typography>

            <Box sx={styles.fulfillmentHeaderChips}>
              <Chip
                label={fulfillmentOrder.status.displayName || fulfillmentOrder.status.name}
                color={getStatusColor(fulfillmentOrder.status.name)}
                size="small"
              />

              <Chip label={feeLabel} color="success" size="small" variant="outlined" />
            </Box>
          </Box>

          <Typography variant="caption" sx={styles.fulfillmentHeaderMeta}>
            {joinElementsWithBullet([
              `${fulfillmentOrder.sender.firstName} ${fulfillmentOrder.sender.lastName}`,
              <a href={`mailto:${fulfillmentOrder.sender.email}`} key="email" style={linkStyles}>
                {fulfillmentOrder.sender.email}
              </a>,
              itemCountLabel,
              ...(weightLabel ? [weightLabel] : [])
            ])}
          </Typography>
        </Box>
      )

      const renderItem = (item: GetTripOfferRequestsForAdminFulfillmentOrderSummary['items'][number]) => (
        <Box key={item.id} sx={styles.fulfillmentItemRow}>
          {item.imageUrls.length > 0 ? (
            <Box component="img" src={item.imageUrls[0]} alt={item.description} sx={styles.fulfillmentItemImage} />
          ) : (
            <Box sx={styles.fulfillmentItemImagePlaceholder}>
              <Inventory2Outlined color="disabled" />
            </Box>
          )}

          <Box sx={styles.fulfillmentItemContent}>
            <Typography variant="subtitle2" sx={styles.fulfillmentItemTitle}>
              {item.description}
            </Typography>

            <Box sx={styles.fulfillmentItemMeta}>
              <Typography variant="caption" sx={styles.fulfillmentItemMetaText}>
                Qty {item.quantity}
              </Typography>

              <Box sx={styles.fulfillmentItemMetaDivider} />

              <Typography variant="caption" sx={styles.fulfillmentItemMetaText}>
                {formatMoney(item.declaredValueCents / 100, fulfillmentOrder.currency)}
              </Typography>

              {(item.fragile || item.repackagingAllowed) && (
                <Box sx={styles.fulfillmentItemMetaChips}>
                  {item.fragile && <Chip label="Fragile" color="warning" size="small" />}
                  {item.repackagingAllowed && <Chip label="Repack OK" variant="outlined" size="small" />}
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      )

      const recipientAddress =
        fulfillmentOrder.recipient &&
        [
          fulfillmentOrder.recipient.streetAddress,
          `${fulfillmentOrder.recipient.city}, ${fulfillmentOrder.recipient.stateProvince} ${fulfillmentOrder.recipient.postalCode}`,
          fulfillmentOrder.recipient.country
        ].join('\n')

      const body = (
        <Box sx={styles.fulfillmentBody}>
          <Box sx={styles.fulfillmentSection}>
            <Typography sx={styles.fulfillmentSectionLabel}>Items</Typography>
            {fulfillmentOrder.items.map(renderItem)}
          </Box>

          {fulfillmentOrder.recipient && (
            <Box sx={styles.fulfillmentSection}>
              <Typography sx={styles.fulfillmentSectionLabel}>Recipient</Typography>

              <Typography variant="body2" sx={styles.fulfillmentRecipientContact}>
                {joinElementsWithBullet([
                  fulfillmentOrder.recipient.name,
                  <a href={`mailto:${fulfillmentOrder.recipient.email}`} key="recipient-email" style={linkStyles}>
                    {fulfillmentOrder.recipient.email}
                  </a>,
                  ...(fulfillmentOrder.recipient.phone ? [fulfillmentOrder.recipient.phone] : [])
                ])}
              </Typography>

              <Box sx={styles.fulfillmentRecipientAddress}>{recipientAddress}</Box>
            </Box>
          )}
        </Box>
      )

      return <Accordion key={offerRequestId} header={{ mainContent: header }} body={body} />
    }

    const renderOfferRequests = () => {
      return offerRequests.map((offerRequest) => {
        if (offerRequest.fulfillmentOrder) {
          return renderFulfillmentOrderAccordion(offerRequest.id, offerRequest.fulfillmentOrder)
        }

        const formattedAmazonCredit = formatMoney(offerRequest.totalItemsCost, offerRequest.currency)
        const formattedTravelerBenefit = formatMoney(offerRequest.totalTravelerBenefit, offerRequest.currency)

        const chips: { label: string; value?: number; color: 'success' | 'error' }[] = [
          { label: `Credit: ${formattedAmazonCredit}`, value: offerRequest.totalItemsCost, color: 'success' },
          { label: `Benefit: ${formattedTravelerBenefit}`, value: offerRequest.totalTravelerBenefit, color: 'success' }
        ]

        const areAllItemRequestsCancelled = offerRequest.itemRequests.every(
          (itemRequest) =>
            itemRequest.status.name === status.itemRequestStatus.cancelledByShopper ||
            itemRequest.status.name === status.itemRequestStatus.deniedByTraveler
        )

        if (areAllItemRequestsCancelled) {
          chips.length = 0
          chips.push({ label: `Cancelled`, color: 'error' })
        }

        const getOfferRequestChips = () => {
          return (
            <Box sx={styles.offerRequestFirstRowChipsContainer}>
              {chips.map((chip) => {
                if ((chip.value && chip.value > 0) || chip.value === undefined) {
                  return <Chip label={chip.label} color={chip.color} size="small" />
                }

                return null
              })}
            </Box>
          )
        }

        return (
          <Accordion
            key={offerRequest.id}
            header={{
              mainContent: (
                <Box sx={styles.accordionHeaderContent}>
                  <Box sx={styles.offerRequestFirstRow}>
                    <Typography variant="body1" sx={styles.offerRequestTitle}>
                      {joinStringsWithBullet(
                        [`Order #${offerRequest.id}`, `${offerRequest.itemRequests.length}`],
                        true
                      )}
                    </Typography>

                    {getOfferRequestChips()}
                  </Box>

                  <Typography variant="caption" sx={styles.shopperInfo}>
                    {joinElementsWithBullet([
                      `${offerRequest.shopper.firstName} ${offerRequest.shopper.lastName}`,
                      `${offerRequest.shopper.id}`,
                      <a href={`mailto:${offerRequest.shopper.email}`} key="email" style={linkStyles}>
                        {offerRequest.shopper.email}
                      </a>
                    ])}
                  </Typography>
                </Box>
              )
            }}
            body={
              <Box sx={styles.itemsContainer}>
                {offerRequest.itemRequests.map((itemRequest) => (
                  <ItemRequestCard key={itemRequest.id} itemRequest={itemRequest} />
                ))}
              </Box>
            }
          />
        )
      })
    }

    if (offerRequests.length > 0) {
      content = renderOfferRequests()
    } else {
      content = <EmptyState title="No offer requests found" />
    }

    return (
      <Box sx={styles.container}>
        <Typography variant="subtitle1" sx={styles.title}>
          {joinElementsWithBullet(['Orders', offerRequests.length.toString()])}
        </Typography>

        {content}
      </Box>
    )
  } else {
    return null
  }
}

export default OfferRequestsAccordion
